import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import type { SupportedMedications } from "../../../catalogs/supported-medications";

import { MedicationGroup } from "../../../api";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { Select } from "../../../components/ui-kit/fields/select";
import { MedicationCombobox } from "./medication-combobox";

/**
 * The medication identity fields — name and group — shared by the add form and
 * the table's edit dialog. A medication is identified in one of three ways, and
 * these pieces cover all of them: a catalog entry (name fixes the group), a
 * free-text name plus a hand-picked group, or a group on its own with no name
 * at all ({@link GROUP_ONLY_NAME}).
 *
 * The two forms lay them out differently — the add form gives the group its own
 * row, the edit dialog stacks everything in one table cell — so the pieces are
 * exported individually rather than as one block.
 */

/**
 * The name stored for a medication entered as a group without a specific drug.
 * The API takes any string for any group (there is no catalog validation), so a
 * blank name would be accepted — but it would read as "not filled in" wherever
 * the name is shown. A literal marker keeps such records legible and searchable.
 * Deliberately untranslated: this is stored data, not display text.
 */
export const GROUP_ONLY_NAME = "Any";

export function isGroupOnly(name: string | null | undefined): boolean {
  return name === GROUP_ONLY_NAME;
}

/** The combobox, replaced by a note once the name is bypassed. */
export const MedicationPicker = observer(function MedicationPicker({
  medications,
  model,
}: MedicationFieldProps) {
  if (isGroupOnly(model.medication_name)) {
    return (
      <span className="text-muted-foreground text-sm">
        <Trans>Group only — no specific medication</Trans>
      </span>
    );
  }

  return (
    <MedicationCombobox
      medications={medications}
      value={model.medication_name}
      onSelect={(group, name) => {
        model.medication_group = group;
        model.medication_name = name;
      }}
      onCustom={(name) => {
        model.medication_name = name;
        model.medication_group = null;
      }}
    />
  );
});

/**
 * The group picker. Offers the groups that accept a free-text name (plus
 * `Other`), which is what a hand-typed medication needs; for a group-only
 * record it widens to the whole catalog, since no name is being claimed and any
 * group is therefore fair game.
 */
export const MedicationGroupSelect = observer(function MedicationGroupSelect({
  medications,
  model,
}: MedicationFieldProps) {
  const { t } = useLingui();
  const groups = isGroupOnly(model.medication_name)
    ? medications.groups
    : medications.freeTextGroups;
  // "Other" is a valid group deliberately kept out of the catalog; offer it as a
  // last-resort bucket for a medication that fits no listed group (rare), and
  // label it so its purpose is clear. The submitted value stays "Other".
  const groupOptions: Record<string, string> = {
    ...Object.fromEntries(groups.map((g) => [g, g])),
    [MedicationGroup.Other]: t`Other (unclassified)`,
  };

  return (
    <Select
      model={model}
      attr="medication_group"
      values={groupOptions}
      listTitle={t`Select a group…`}
    />
  );
});

/**
 * Bypasses the name entirely, for a medication known only by its group. Ticking
 * it drops whatever name was chosen; unticking clears the name back to nothing
 * so the combobox starts empty rather than showing the marker.
 */
export const GroupOnlyCheckbox = observer(function GroupOnlyCheckbox({
  model,
  label,
}: {
  model: MedicationIdentity;
  /**
   * Visible label. Omit where the surrounding form already labels the control —
   * the add form gives it its own `FormRow` — and it falls back to naming the
   * checkbox for assistive tech only.
   */
  label?: string;
}) {
  const { t } = useLingui();

  return (
    <Checkbox
      label={label}
      ariaLabel={label == null ? t`Group only` : undefined}
      value={isGroupOnly(model.medication_name)}
      onChange={(checked) => {
        model.medication_name = checked ? GROUP_ONLY_NAME : null;
      }}
    />
  );
});

export const groupOnlyHint = (
  <Trans>
    For a medication whose group is known but the specific drug isn’t. The name
    is stored as <strong>{GROUP_ONLY_NAME}</strong> and the group can be picked
    from the whole catalog, not just the free-text ones.
  </Trans>
);

/**
 * Whether the group has to be picked by hand: either the name is free text the
 * combobox couldn't match to a catalog entry (the combobox only sets the group
 * for matches), or there is no name to derive it from.
 */
export function needsGroupSelection(
  medications: SupportedMedications,
  model: MedicationIdentity,
): boolean {
  if (model.medication_name == null) return false;
  if (isGroupOnly(model.medication_name)) return true;
  return !(
    model.medication_group != null &&
    medications.isKnownMedication(model.medication_group, model.medication_name)
  );
}

export type MedicationIdentity = {
  medication_group: string | null;
  medication_name: string | null;
};

type MedicationFieldProps = {
  medications: SupportedMedications;
  model: MedicationIdentity;
};
